import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
  ActivityIndicator,
  Clipboard,
  Platform,
  Pressable,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import styled from 'styled-components/native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { REDESIGN_COLORS } from '../../../styles/colors';
import logger from '../../../utils/logger';
import TypingIndicator from '../TypingIndicator';

/**
 * DraftBlock — Sticky decision block at the bottom of the chat (desktop).
 *
 * Surfaces the AI's current draft and the user's available actions in a
 * single self-contained component. Wraps subject + body + ZOPA strip +
 * reasoning panel + actions row. Returns null for `'needs_input'` because
 * the orchestrator renders `NeedsInputPrompt` instead.
 *
 * Props:
 *   activeDrafts        Array of agent_draft messages in the latest active round.
 *                       Each carries `{id, subject, content, draft_version, round_number,
 *                       agent_reasoning, status, proposed_payload?}`.
 *   selectedVersionId   Currently selected draft id. The component highlights its tab.
 *   onSelectVersion     (draftId) => void
 *   onApprove           (draftId) => void
 *   onEdit              (draftId) => void — legacy modal trigger; optional now
 *                       that inline-edit (onSaveEdit) is the default flow.
 *   onSaveEdit          (draftId, { subject, content }) => Promise<void>
 *                       Inline-save handler. Called when the user finishes
 *                       editing the draft directly in the suggested-message box.
 *   onRegenerate        () => void
 *   onDiscard           (draftId) => void
 *   onReport            (draftId) => void
 *   onEnd               () => void — opens the End-negotiation confirmation
 *   regenCount          number — drafts already generated this round
 *   regenMax            number — cap (`MAX_DRAFTS_PER_ROUND`)
 *   negotiation         negotiation row (used for ZOPA mapping)
 *   surface             'draft' | 'guardrail' | 'needs_input' | 'generating'
 */
const DraftBlock = ({
  activeDrafts = [],
  selectedVersionId,
  onSelectVersion,
  onApprove,
  onEdit,
  onSaveEdit,
  onRegenerate,
  onDiscard,
  onReport,
  onEnd,
  regenCount,
  regenMax,
  negotiation,
  surface,
}) => {
  const { width: windowWidth } = useWindowDimensions();
  const isCompact = windowWidth < 768;

  const selectedDraft = useMemo(() => {
    if (!Array.isArray(activeDrafts) || activeDrafts.length === 0) return null;
    return (
      activeDrafts.find((d) => d.id === selectedVersionId) ||
      activeDrafts[activeDrafts.length - 1]
    );
  }, [activeDrafts, selectedVersionId]);

  // Multiple versions in the same round get tabs (V1, V2, …).
  const versionTabs = useMemo(() => {
    if (!Array.isArray(activeDrafts) || activeDrafts.length === 0) return [];
    const latestRound = Math.max(
      ...activeDrafts.map((d) => d.round_number || 0)
    );
    const sameRound = activeDrafts
      .filter((d) => (d.round_number || 0) === latestRound)
      .sort((a, b) => (a.draft_version || 0) - (b.draft_version || 0));
    if (sameRound.length <= 1) return [];
    return sameRound.map((d, i) => ({
      id: d.id,
      label: `V${d.draft_version || i + 1}`,
    }));
  }, [activeDrafts]);

  // Local in-flight flags. The worker does not insert placeholder rows for
  // either regenerate or send, so `surface` stays 'draft' during the
  // backend round-trip. Without these flags the user gets no visual feedback
  // after clicking the action buttons. Each flag anchors on the selected
  // draft id at click time and clears when the realtime subscription
  // delivers the state change, with a safety timeout as backstop.
  const [regenInFlight, setRegenInFlight] = useState(false);
  const regenAnchorIdRef = useRef(null);
  const regenTimeoutRef = useRef(null);
  const latestDraftId = activeDrafts?.[activeDrafts.length - 1]?.id || null;

  const [approveInFlight, setApproveInFlight] = useState(false);
  const approveAnchorIdRef = useRef(null);
  const approveTimeoutRef = useRef(null);

  // Regenerate clears when a *new* draft id appears in activeDrafts.
  useEffect(() => {
    if (!regenInFlight) return;
    if (latestDraftId && latestDraftId !== regenAnchorIdRef.current) {
      setRegenInFlight(false);
      if (regenTimeoutRef.current) {
        clearTimeout(regenTimeoutRef.current);
        regenTimeoutRef.current = null;
      }
    }
  }, [latestDraftId, regenInFlight]);

  // Approve clears when the anchored draft drops out of activeDrafts (its
  // status moved out of 'draft' / 'guardrail_blocked' / 'generating' /
  // 'needs_user_input' — i.e. it was sent, discarded, or otherwise resolved).
  useEffect(() => {
    if (!approveInFlight) return;
    const stillActive = !!activeDrafts?.find(
      (d) => d.id === approveAnchorIdRef.current,
    );
    if (!stillActive) {
      setApproveInFlight(false);
      if (approveTimeoutRef.current) {
        clearTimeout(approveTimeoutRef.current);
        approveTimeoutRef.current = null;
      }
    }
  }, [activeDrafts, approveInFlight]);

  useEffect(
    () => () => {
      if (regenTimeoutRef.current) clearTimeout(regenTimeoutRef.current);
      if (approveTimeoutRef.current) clearTimeout(approveTimeoutRef.current);
    },
    [],
  );

  const isGenerating = surface === 'generating' || regenInFlight;
  const isGuardrail = surface === 'guardrail';
  // Button shows the spinner whenever an action is in flight; the body keeps
  // its existing semantics (only swaps to TypingIndicator for regenerate).
  const isActionInFlight = isGenerating || approveInFlight;
  const approveDisabled =
    isActionInFlight || isGuardrail || !selectedDraft;
  const overflowDisabled = isActionInFlight || !selectedDraft;

  // Pull the guardrail block reason from `agent_reasoning` (matches the
  // existing screen-side regex contract emitted by the worker).
  const guardrailReason = useMemo(() => {
    if (!isGuardrail || !selectedDraft) return null;
    // Prefer an explicit field if the worker ever sets one; fall back to regex.
    const explicit =
      selectedDraft.guardrail_reason ||
      selectedDraft.proposed_payload?.guardrail_reason ||
      null;
    if (explicit) return explicit;
    const reasoning = selectedDraft.agent_reasoning || '';
    const m =
      reasoning.match(/Guardrail blocked:\s*([^\n]+)/i) ||
      reasoning.match(/Safety eval failed\s*\(([^)]+)\)[^\n]*/i);
    return m ? m[1].trim() : null;
  }, [isGuardrail, selectedDraft]);

  // ---------- Overflow menu (Edit / Regenerate / Discard) ----------

  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const menuRef = useRef(null);

  // Close on outside-click (web) and on Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    if (Platform.OS !== 'web' || typeof document === 'undefined') return undefined;
    const handler = (e) => {
      const node = menuRef.current?._node || menuRef.current;
      if (node && typeof node.contains === 'function' && node.contains(e.target)) return;
      closeMenu();
    };
    const keyHandler = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [menuOpen, closeMenu]);

  const canRegenerate =
    typeof regenCount === 'number' && typeof regenMax === 'number'
      ? regenCount < regenMax
      : true;

  const handleApprove = useCallback(() => {
    if (approveDisabled) return;
    approveAnchorIdRef.current = selectedDraft.id;
    setApproveInFlight(true);
    if (approveTimeoutRef.current) clearTimeout(approveTimeoutRef.current);
    // Worst-case backstop: send-via-SES is usually <5s but we keep the same
    // 60s ceiling as regenerate for consistency.
    approveTimeoutRef.current = setTimeout(() => {
      setApproveInFlight(false);
      approveTimeoutRef.current = null;
    }, 60000);
    onApprove?.(selectedDraft.id);
  }, [approveDisabled, onApprove, selectedDraft]);

  // ---------- Inline edit state ----------
  // Tapping the body (or the Edit overflow item) flips the message into edit
  // mode. The original draft id is anchored so that if a *new* draft arrives
  // while editing, we drop the in-flight edit (it would be saved against a
  // stale id otherwise).
  const [editing, setEditing] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const editAnchorIdRef = useRef(null);

  const enterEdit = useCallback(() => {
    if (!selectedDraft) return;
    editAnchorIdRef.current = selectedDraft.id;
    setEditSubject(
      selectedDraft.subject ||
        selectedDraft.proposed_payload?.subject ||
        ''
    );
    setEditBody(
      selectedDraft.content ||
        selectedDraft.proposed_payload?.body ||
        selectedDraft.body ||
        ''
    );
    setEditing(true);
  }, [selectedDraft]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setSavingEdit(false);
    editAnchorIdRef.current = null;
  }, []);

  // If the user switches version or a new draft arrives mid-edit, abort so we
  // never save edits against a stale draft id.
  useEffect(() => {
    if (!editing) return;
    if (!selectedDraft || selectedDraft.id !== editAnchorIdRef.current) {
      cancelEdit();
    }
  }, [editing, selectedDraft, cancelEdit]);

  const handleSaveInline = useCallback(async () => {
    if (!selectedDraft || !onSaveEdit || savingEdit) return;
    const trimmedBody = (editBody || '').trim();
    if (!trimmedBody) return;
    setSavingEdit(true);
    try {
      await onSaveEdit(selectedDraft.id, {
        subject: (editSubject || '').trim() || null,
        content: trimmedBody,
      });
      // Parent will discard / replace the draft → useEffect above clears the
      // edit anchor and exits edit mode. Belt-and-braces in case it doesn't.
      setEditing(false);
    } catch (err) {
      logger.error('[DraftBlock] inline save failed', err);
    } finally {
      setSavingEdit(false);
    }
  }, [editBody, editSubject, onSaveEdit, savingEdit, selectedDraft]);

  const handleEdit = useCallback(() => {
    closeMenu();
    if (!selectedDraft) return;
    // Prefer inline edit when the parent wires onSaveEdit; fall back to the
    // legacy modal trigger only when no inline handler is provided.
    if (onSaveEdit) {
      enterEdit();
    } else if (onEdit) {
      onEdit(selectedDraft.id);
    }
  }, [closeMenu, enterEdit, onEdit, onSaveEdit, selectedDraft]);

  const handleRegenerate = useCallback(() => {
    closeMenu();
    if (!canRegenerate) return;
    regenAnchorIdRef.current = latestDraftId;
    setRegenInFlight(true);
    if (regenTimeoutRef.current) clearTimeout(regenTimeoutRef.current);
    // Safety cap above the worst-case backend budget: Bedrock 30s timeout +
    // OpenRouter fallback ~30s = ~60s end-to-end. Clears the spinner if the
    // realtime subscription drops the new-draft event.
    regenTimeoutRef.current = setTimeout(() => {
      setRegenInFlight(false);
      regenTimeoutRef.current = null;
    }, 60000);
    onRegenerate?.();
  }, [closeMenu, onRegenerate, canRegenerate, latestDraftId]);

  const handleDiscard = useCallback(() => {
    closeMenu();
    if (!selectedDraft) return;
    onDiscard?.(selectedDraft.id);
  }, [closeMenu, onDiscard, selectedDraft]);

  const handleReport = useCallback(() => {
    if (!selectedDraft) return;
    onReport?.(selectedDraft.id);
  }, [onReport, selectedDraft]);

  const handleEnd = useCallback(() => {
    closeMenu();
    onEnd?.();
  }, [closeMenu, onEnd]);

  // Copy-script button (script-handoff mode only). The script + talking points
  // come from the parsed coachingScript below; we re-derive them inside the
  // handler so the callback identity stays stable.
  const [scriptCopied, setScriptCopied] = useState(false);
  const scriptCopyTimerRef = useRef(null);
  useEffect(
    () => () => {
      if (scriptCopyTimerRef.current) clearTimeout(scriptCopyTimerRef.current);
    },
    []
  );

  const handleCopyScript = useCallback(async (text) => {
    if (!text) return;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        Clipboard.setString(text);
      }
      setScriptCopied(true);
      if (scriptCopyTimerRef.current) clearTimeout(scriptCopyTimerRef.current);
      scriptCopyTimerRef.current = setTimeout(() => setScriptCopied(false), 2000);
    } catch (err) {
      logger.warn('[DraftBlock] script clipboard copy failed', err);
    }
  }, []);

  // ---------- Render ----------

  // Orchestrator renders NeedsInputPrompt instead of DraftBlock.
  if (surface === 'needs_input') return null;

  const subject =
    selectedDraft?.subject ||
    selectedDraft?.proposed_payload?.subject ||
    null;
  const rawBody =
    selectedDraft?.content ||
    selectedDraft?.proposed_payload?.body ||
    selectedDraft?.body ||
    '';

  // Guided/script-handoff drafts arrive as JSON: {coachingText, talkingPoints}.
  // Detect by leading `{` and parse; render as a coaching script instead of
  // dumping the JSON string into the body.
  let coachingScript = null;
  if (typeof rawBody === 'string' && rawBody.startsWith('{')) {
    try {
      const parsed = JSON.parse(rawBody);
      if (parsed && typeof parsed === 'object' && parsed.coachingText) {
        coachingScript = parsed;
      }
    } catch {}
  }
  const body = coachingScript ? '' : rawBody;

  const headerLabel = coachingScript
    ? 'Your script for this dealer'
    : isGuardrail
      ? 'Draft (held back)'
      : 'Suggested message';

  return (
    <Container accessibilityLabel="Draft block">
      {/* ---------- Header row ---------- */}
      <HeaderRow>
        <HeaderLabel>{headerLabel}</HeaderLabel>

        {versionTabs.length > 0 && (
          <TabsRow>
            {versionTabs.map((tab) => {
              const active = selectedDraft && tab.id === selectedDraft.id;
              return (
                <VersionTab
                  key={tab.id}
                  $active={!!active}
                  onPress={() => onSelectVersion?.(tab.id)}
                  accessibilityLabel={`Show draft version ${tab.label}`}
                >
                  <VersionTabText $active={!!active}>{tab.label}</VersionTabText>
                </VersionTab>
              );
            })}
          </TabsRow>
        )}

        <HeaderSpacer />

        {typeof regenCount === 'number' && typeof regenMax === 'number' ? (
          <RegenCounter accessibilityLabel={`Regenerations used: ${regenCount} of ${regenMax}`}>
            {regenCount}/{regenMax}
          </RegenCounter>
        ) : null}

        {coachingScript ? (
          <CopyScriptButton
            $compact={isCompact}
            onPress={() => {
              const text = (coachingScript.coachingText || '')
                .replace(/^[""]|[""]$/g, '')
                .trim();
              handleCopyScript(text);
            }}
            accessibilityLabel="Copy the script"
          >
            <MaterialCommunityIcons
              name={scriptCopied ? 'check' : 'content-copy'}
              size={isCompact ? 18 : 16}
              color={REDESIGN_COLORS.textPrimary}
            />
            {!isCompact ? (
              <CopyScriptButtonText>
                {scriptCopied ? 'Copied' : 'Copy script'}
              </CopyScriptButtonText>
            ) : null}
          </CopyScriptButton>
        ) : null}

        <ReportButton
          onPress={handleReport}
          disabled={!selectedDraft || isGenerating}
          accessibilityLabel="Report this draft"
        >
          <MaterialCommunityIcons
            name="flag-outline"
            size={16}
            color={REDESIGN_COLORS.textSecondary}
          />
          <ReportButtonText>Report</ReportButtonText>
        </ReportButton>
      </HeaderRow>

      {/* ---------- Body ---------- */}
      {isGenerating ? (
        <GeneratingWrap accessibilityLabel="Composing reply">
          <TypingIndicator />
          <GeneratingNote>Composing reply...</GeneratingNote>
        </GeneratingWrap>
      ) : coachingScript ? (
        <BodyWrap $muted={isGuardrail}>
          <BodyText>
            {(coachingScript.coachingText || '').replace(/^[""]|[""]$/g, '').trim()}
          </BodyText>
        </BodyWrap>
      ) : editing ? (
        <BodyWrap $muted={false} $editing>
          {subject ? (
            <SubjectRow>
              <SubjectLabel>Subject</SubjectLabel>
              <SubjectInput
                value={editSubject}
                onChangeText={setEditSubject}
                placeholder="Subject"
                placeholderTextColor={REDESIGN_COLORS.textSecondary}
                editable={!savingEdit}
              />
            </SubjectRow>
          ) : null}
          <BodyInput
            value={editBody}
            onChangeText={setEditBody}
            placeholder="Write your message..."
            placeholderTextColor={REDESIGN_COLORS.textSecondary}
            multiline
            editable={!savingEdit}
            autoFocus
          />
        </BodyWrap>
      ) : (
        <BodyPressable
          onPress={onSaveEdit ? enterEdit : undefined}
          disabled={!selectedDraft || !onSaveEdit}
          accessibilityRole={onSaveEdit ? 'button' : undefined}
          accessibilityLabel={onSaveEdit ? 'Edit this message' : undefined}
          accessibilityHint={onSaveEdit ? 'Tap to edit the message inline' : undefined}
        >
          <BodyWrap $muted={isGuardrail}>
            {subject ? (
              <SubjectRow>
                <SubjectLabel>Subject</SubjectLabel>
                <SubjectText numberOfLines={2}>{subject}</SubjectText>
              </SubjectRow>
            ) : null}
            {body ? (
              <BodyText>{body}</BodyText>
            ) : (
              <BodyEmpty>No draft body yet.</BodyEmpty>
            )}
          </BodyWrap>
        </BodyPressable>
      )}

      {/* ---------- Guardrail callout (above actions) ---------- */}
      {isGuardrail ? (
        <GuardrailCallout>
          <MaterialCommunityIcons
            name="shield-alert-outline"
            size={16}
            color={REDESIGN_COLORS.scoreMedium}
          />
          <GuardrailContent>
            <GuardrailTitle>Held back by your safety guardrail</GuardrailTitle>
            {guardrailReason ? (
              <GuardrailReasonText>{guardrailReason}</GuardrailReasonText>
            ) : null}
            <GuardrailHint>
              Edit, regenerate, or discard this draft. Sending is disabled.
            </GuardrailHint>
          </GuardrailContent>
        </GuardrailCallout>
      ) : null}

      {/* ---------- Actions row ---------- */}
      <ActionsRow>
        {editing ? (
          <>
            <CancelButton
              onPress={cancelEdit}
              disabled={savingEdit}
              accessibilityLabel="Cancel editing"
            >
              <CancelButtonText>Cancel</CancelButtonText>
            </CancelButton>
            <ApproveButton
              onPress={handleSaveInline}
              disabled={savingEdit || !(editBody || '').trim()}
              $disabled={savingEdit || !(editBody || '').trim()}
              accessibilityLabel="Save edited message and send"
              accessibilityState={{ busy: savingEdit }}
            >
              {savingEdit ? (
                <ActivityIndicator size="small" color="#ffffff" />
              ) : (
                <ApproveButtonText>Save &amp; send</ApproveButtonText>
              )}
            </ApproveButton>
          </>
        ) : (
          <ApproveButton
            onPress={handleApprove}
            disabled={approveDisabled}
            $disabled={approveDisabled}
            accessibilityLabel={
              isGenerating
                ? 'Generating draft, please wait'
                : approveInFlight
                  ? coachingScript
                    ? 'Recording confirmation, please wait'
                    : 'Sending draft, please wait'
                  : coachingScript
                    ? "I've sent the script to the dealer"
                    : 'Approve and send this draft'
            }
            accessibilityState={{ busy: isActionInFlight, disabled: approveDisabled }}
          >
            {isActionInFlight ? (
              <ActivityIndicator size="small" color="#ffffff" />
            ) : (
              <ApproveButtonText>
                {coachingScript ? "I've sent it" : 'Approve & send'}
              </ApproveButtonText>
            )}
          </ApproveButton>
        )}

        {/* Overflow menu — hidden during inline edit */}
        {editing ? null : (
        <OverflowAnchor ref={menuRef}>
          <OverflowButton
            onPress={() => setMenuOpen((v) => !v)}
            disabled={overflowDisabled}
            accessibilityLabel="More draft actions"
          >
            <MaterialCommunityIcons
              name="dots-vertical"
              size={18}
              color={REDESIGN_COLORS.textPrimary}
            />
          </OverflowButton>
          {menuOpen ? (
            <OverflowMenu>
              <MenuItem onPress={handleEdit}>
                <MaterialCommunityIcons
                  name="pencil-outline"
                  size={16}
                  color={REDESIGN_COLORS.textPrimary}
                />
                <MenuItemText>Edit</MenuItemText>
              </MenuItem>
              <MenuItem
                onPress={handleRegenerate}
                disabled={!canRegenerate}
                $disabled={!canRegenerate}
              >
                <MaterialCommunityIcons
                  name="refresh"
                  size={16}
                  color={
                    canRegenerate
                      ? REDESIGN_COLORS.textPrimary
                      : REDESIGN_COLORS.textSecondary
                  }
                />
                <MenuItemText $disabled={!canRegenerate}>
                  Regenerate{typeof regenCount === 'number' && typeof regenMax === 'number'
                    ? ` (${regenCount}/${regenMax})`
                    : ''}
                </MenuItemText>
              </MenuItem>
              <MenuItem onPress={handleDiscard}>
                <MaterialCommunityIcons
                  name="trash-can-outline"
                  size={16}
                  color={REDESIGN_COLORS.textPrimary}
                />
                <MenuItemText>Discard</MenuItemText>
              </MenuItem>
              {onEnd ? (
                <>
                  <MenuDivider />
                  <MenuItem onPress={handleEnd}>
                    <MaterialCommunityIcons
                      name="close-circle-outline"
                      size={16}
                      color="#FF4D6D"
                    />
                    <MenuItemText $danger>End negotiation</MenuItemText>
                  </MenuItem>
                </>
              ) : null}
            </OverflowMenu>
          ) : null}
        </OverflowAnchor>
        )}
      </ActionsRow>
    </Container>
  );
};

// ---------- Styled components ----------

const Container = styled.View`
  border-radius: 12px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  padding: 14px;
  gap: 12px;
`;

const HeaderRow = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const HeaderLabel = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 12px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${REDESIGN_COLORS.textSecondary};
`;

const TabsRow = styled.View`
  flex-direction: row;
  gap: 6px;
`;

const VersionTab = styled(Pressable)`
  padding: 4px 10px;
  border-radius: 999px;
  background-color: ${({ $active }) =>
    $active ? 'rgba(5, 86, 247, 0.20)' : 'rgba(255, 255, 255, 0.06)'};
  border-width: 1px;
  border-color: ${({ $active }) =>
    $active ? REDESIGN_COLORS.ctaBlue : REDESIGN_COLORS.borderSubtle};
`;

const VersionTabText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 12px;
  color: ${({ $active }) =>
    $active ? '#FFFFFF' : REDESIGN_COLORS.textSecondary};
`;

const HeaderSpacer = styled.View`
  flex: 1;
`;

const RegenCounter = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 12px;
  color: ${REDESIGN_COLORS.textSecondary};
`;

const ReportButton = styled(Pressable)`
  flex-direction: row;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.06);
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
`;

const ReportButtonText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 12px;
  color: ${REDESIGN_COLORS.textSecondary};
`;

const CopyScriptButton = styled(Pressable)`
  flex-direction: row;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? '0px' : '4px')};
  padding: ${({ $compact }) => ($compact ? '6px' : '6px 10px')};
  border-radius: 8px;
  background-color: rgba(5, 86, 247, 0.12);
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.ctaBlue};
`;

const CopyScriptButtonText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 12px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const BodyWrap = styled.View`
  background-color: rgba(255, 255, 255, 0.04);
  border-radius: 10px;
  padding: 12px;
  gap: 8px;
  opacity: ${({ $muted }) => ($muted ? 0.55 : 1)};
  border-width: 1px;
  border-color: ${({ $editing }) =>
    $editing ? REDESIGN_COLORS.ctaBlue : 'transparent'};
`;

const BodyPressable = styled(Pressable)`
  border-radius: 10px;
`;

const SubjectRow = styled.View`
  flex-direction: column;
  gap: 2px;
`;

const SubjectLabel = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 10px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${REDESIGN_COLORS.textSecondary};
`;

const SubjectText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 14px;
  line-height: 20px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const SubjectInput = styled(TextInput).attrs({
  underlineColorAndroid: 'transparent',
})`
  font-family: 'Satoshi-Bold';
  font-size: 14px;
  line-height: 20px;
  color: ${REDESIGN_COLORS.textPrimary};
  padding: 0;
  margin: 0;
  background-color: transparent;
`;

const BodyText = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 14px;
  line-height: 20px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const BodyInput = styled(TextInput).attrs({
  underlineColorAndroid: 'transparent',
})`
  font-family: 'Satoshi-Regular';
  font-size: 14px;
  line-height: 20px;
  color: ${REDESIGN_COLORS.textPrimary};
  padding: 0;
  margin: 0;
  min-height: 292px;
  background-color: transparent;
  text-align-vertical: top;
`;

const SectionHeader = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 12px;
  color: ${REDESIGN_COLORS.ctaBlue};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const BodyEmpty = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 13px;
  font-style: italic;
  color: ${REDESIGN_COLORS.textSecondary};
`;

const GeneratingWrap = styled.View`
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const GeneratingNote = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 12px;
  color: ${REDESIGN_COLORS.textSecondary};
  padding: 0 16px 8px;
`;

const GuardrailCallout = styled.View`
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  background-color: ${REDESIGN_COLORS.scoreMediumBg};
  border-radius: 10px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.scoreMedium};
  padding: 10px 12px;
`;

const GuardrailContent = styled.View`
  flex: 1;
  gap: 4px;
`;

const GuardrailTitle = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 13px;
  color: ${REDESIGN_COLORS.scoreMedium};
`;

const GuardrailReasonText = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 13px;
  line-height: 18px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const GuardrailHint = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 12px;
  color: ${REDESIGN_COLORS.textSecondary};
`;

const ActionsRow = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const ApproveButton = styled(Pressable)`
  flex: 1;
  background-color: ${({ $disabled }) =>
    $disabled ? 'rgba(5, 86, 247, 0.30)' : REDESIGN_COLORS.ctaBlue};
  border-radius: 8px;
  padding: 12px 16px;
  align-items: center;
  justify-content: center;
`;

const ApproveButtonText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 14px;
  line-height: 20px;
  color: #ffffff;
`;

const CancelButton = styled(Pressable)`
  padding: 12px 16px;
  border-radius: 8px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  background-color: transparent;
  align-items: center;
  justify-content: center;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const CancelButtonText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 14px;
  line-height: 20px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const OverflowAnchor = styled.View`
  position: relative;
`;

const OverflowButton = styled(Pressable)`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.08);
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  align-items: center;
  justify-content: center;
`;

const OverflowMenu = styled.View`
  position: absolute;
  right: 0;
  bottom: 52px;
  min-width: 200px;
  background-color: ${REDESIGN_COLORS.cardBg};
  border-radius: 10px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  padding: 6px;
  gap: 2px;
  z-index: 30;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
`;

const MenuItem = styled(Pressable)`
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

const MenuItemText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 13px;
  color: ${({ $disabled, $danger }) => {
    if ($disabled) return REDESIGN_COLORS.textSecondary;
    if ($danger) return '#FF4D6D';
    return REDESIGN_COLORS.textPrimary;
  }};
`;

const MenuDivider = styled.View`
  height: 1px;
  background-color: ${REDESIGN_COLORS.borderSubtle};
  margin: 4px -2px;
`;

export default DraftBlock;
